"use client";
import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 10;
      const cards = heroRef.current.querySelectorAll<HTMLElement>(".parallax-card");
      cards.forEach((card, i) => {
        const depth = (i + 1) * 0.3;
        card.style.transform = `translate(${x * depth}px, ${y * depth}px)`;
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <>
      {/* Launch Offer Banner */}
      <div className="w-full bg-gradient-to-r from-violet-600/90 via-blue-600/90 to-violet-600/90 border-b border-violet-500/30 py-2.5 px-4 text-center z-50 relative">
        <p className="text-xs sm:text-sm font-mono text-white/90 tracking-wide">
          🚀 <span className="font-bold text-white">Launch Angebot</span> – Die ersten Kunden erhalten kostenlos:{" "}
          <span className="text-yellow-300 font-semibold">BIOS Update &amp; Gaming Optimierung</span>.
          {" "}<span className="text-white/70">Nur für begrenzte Zeit.</span>
        </p>
      </div>

      <header ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1e1c86bec-1772450994020.png"
            alt="High-end gaming PC build with RGB lighting and clean cable management"
            fill
            priority
            className="object-cover object-center"
            sizes="100vw" />
          
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080F] via-[#08080F]/70 to-[#08080F]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#08080F]/80 via-transparent to-[#08080F]/40" />
        </div>

        {/* Orbs */}
        <div className="orb w-[600px] h-[600px] bg-violet-500/10 top-1/4 -left-40 z-0" />
        <div className="orb w-[400px] h-[400px] bg-blue-500/10 top-1/3 right-0 z-0" />

        {/* Grid overlay */}
        <div className="absolute inset-0 grid-bg z-0 opacity-40" />

        {/* Floating Cards */}
        <div className="absolute top-32 right-8 lg:right-24 z-20 hidden md:block parallax-card transition-transform duration-300 ease-out float-anim">
          <div className="glass rounded-xl p-5 max-w-[200px]">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="font-mono text-[10px] text-green-400 uppercase tracking-widest">System Online</span>
            </div>
            <div className="font-mono text-xs text-white/50 space-y-1">
              <div>CPU: <span className="text-violet-400">100%</span></div>
              <div>GPU: <span className="text-blue-400">98%</span></div>
              <div>RAM: <span className="text-violet-400">32GB</span></div>
            </div>
          </div>
        </div>

        <div className="absolute top-48 right-8 lg:right-52 z-20 hidden lg:block parallax-card transition-transform duration-300 ease-out float-anim-delayed">
          <div className="glass rounded-xl p-4 max-w-[180px]">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-2">Build Status</div>
            <div className="text-sm font-display font-bold text-white">Premium Build</div>
            <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full w-[92%] rounded-full bg-gradient-to-r from-violet-500 to-blue-500" />
            </div>
            <div className="font-mono text-[10px] text-violet-400 mt-1">92% optimiert</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-24 pt-32">
          <div className="max-w-3xl">
            {/* Label */}
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8 bg-violet-500" />
              <span className="font-mono text-xs text-violet-400 uppercase tracking-widest">PC Build Service · Schweiz</span>
              <div className="h-px w-8 bg-violet-500" />
            </div>

            {/* Headline */}
            <h1 className="font-display font-extrabold text-[clamp(2.8rem,7vw,5.5rem)] leading-[0.9] tracking-tight text-white mb-6">
              Professioneller{" "}
              <span className="gradient-text">PC-Zusammenbau</span>
              <br />
              in der{" "}
              <span className="text-glow">Schweiz</span>
            </h1>

            {/* SEO Subtext */}
            <p className="text-[clamp(0.85rem,1.5vw,1rem)] text-violet-300/70 leading-relaxed max-w-2xl mb-4 font-body">
              Professioneller PC-Zusammenbau in der Schweiz – sauberes Kabelmanagement, Windows Setup, BIOS Updates und Gaming Optimierung für maximale Performance.
            </p>

            {/* Subheadline */}
            <p className="text-[clamp(1rem,2vw,1.2rem)] text-white/60 leading-relaxed max-w-2xl mb-12 font-body">
              Sauberer Aufbau, gutes Kabelmanagement, Windows-Installation, Treiber, BIOS-Update und Gaming-Optimierung – alles aus einer Hand.
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <a href="#preise" className="btn-primary inline-block text-center">
                Preise ansehen
              </a>
              <a href="#kontakt" className="btn-secondary inline-block text-center">
                Kontakt aufnehmen
              </a>
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-8">
              {[
              { value: "5+", label: "Jahre PC Erfahrung" },
              { value: "100%", label: "Kundenzufriedenheit" },
              { value: "3", label: "Pakete verfügbar" }].
              map((stat) =>
              <div key={stat.label} className="flex flex-col">
                  <span className="font-display font-extrabold text-2xl gradient-text">{stat.value}</span>
                  <span className="font-mono text-[10px] text-white/35 uppercase tracking-widest mt-1">{stat.label}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2">
          <span className="font-mono text-[10px] text-white/25 uppercase tracking-widest">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/25 to-transparent" />
        </div>
      </header>
    </>);

}